/* eslint-disable no-sequences */
import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Field } from "formik";
import { TextField } from "formik-material-ui";
import NumberFormat from "react-number-format";
import { IconButton, InputAdornment, Switch } from "@material-ui/core";
import { Delete, Edit } from "@material-ui/icons";
import { Colors, MaterialQtdMode } from "../../../../constants";
import { mediaHelper } from "../../../../utils";
import { MaterialFractionated } from "../../../../constants/MaterialFractionated";

const Container = styled.div`
  display: flex;
  position: relative;
  padding: 18px 24px;
  flex-wrap: wrap;
  gap: 32px;

  ${mediaHelper.md`
    position: static;
  `}

  ${mediaHelper.lg`
    justify-content: center;
    flex-direction: row;
  `}
`;

const ControlWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 15px 0 0 0;
  flex: 1;

  ${mediaHelper.md`
    margin: 0px;
  `}
`;

const UnitsWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding-right: 28px;
  width: 266px;
  gap: 4px;

  ${mediaHelper.md`
    padding-right: 0;
  `}
`;

const ValueText = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${Colors.GRAY_DARKEST};
  max-width: 266px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ error }) => error && `color: ${Colors.DANGER}`}
`;

const SwitchModeContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-right: 24px;
`;

const Title = styled.div`
  font-size: 16px;
  color: ${Colors.GRAY_DARKEST};
  font-weight: bold;
  margin-bottom: 4px;
  max-width: 266px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledLabel = styled.div`
  font-size: 12px;
  color: ${Colors.GRAY_DARKEST};
  font-weight: bold;

  ${({ error }) => error && `color: ${Colors.DANGER}`}
`;

const SwitchLabel = styled.span`
  font-size: 12px;
  color: ${Colors.GRAY_DARKEST};

  ${({ selected }) =>
    selected &&
    `
    color: ${Colors.PRIMARY_BLUE};
    font-weight: bold;
  `}
`;

const StyledTextField = styled(Field)`
  width: 100%;
  padding-right: 0px;

  .MuiOutlinedInput-adornedEnd {
    padding-right: 0px !important;
  }
`;

const ActionButtonContainer = styled.div`
  padding-top: 4px;
  position: absolute;
  top: 0;
  right: 12px;

  ${mediaHelper.md`
    position: static;
  `}
`;

const StyledInputAdornment = styled(InputAdornment)`
  padding-right: 0px;
  & > * {
    border-radius: 0 !important;
  }
`;

const StyledIconButton = styled(IconButton)`
  border-radius: 0 !important;
  width: 42px;

  &.MuiIconButton-edgeEnd {
    margin-right: 0 !important;
  }
`;

const StyledDecreaseIconButton = styled(IconButton)`
  border-radius: 0 !important;
  border-left: 1px solid ${Colors.GRAY_LIGHT};
  border-right: 1px solid ${Colors.GRAY_LIGHT};
  width: 40px;

  &.MuiIconButton-edgeEnd {
    margin-right: 0 !important;
  }
`;

const backgroundColorTypeLabeled = {
  [MaterialFractionated.LABELED]: Colors.PRIMARY_BLUE,
};

const StyledTypeOfLabeled = styled.div`
  font-size: 12px;
  color: ${Colors.WHITE};
  background-color: ${({ type }) =>
    backgroundColorTypeLabeled[type] || Colors.WARNING_DARK};
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 16px;
  width: max-content;
`;

function SelectedMaterialRow({
  title,
  canEdit,
  availableCount,
  qtdMode,
  total,
  qty,
  agentTotal,
  handleRemove,
  handleEdit,
  handleQtdChange,
  handleModeChange,
  isAgentView,
  materialLabeled,
  materialPackingQuantity,
  handleIncrease,
  handleDecrease,
  isMaterialFieldError,
  messageHelperText,
  ...props
}) {
  return (
    <Container {...props}>
      <UnitsWrapper>
        <Title>{title}</Title>
        <ValueText>
          {"Quantidade disponível: "}
          <NumberFormat
            value={availableCount || 0}
            displayType="text"
            decimalSeparator=","
            thousandSeparator={(true, ".")}
          />
          {" disponíveis"}
        </ValueText>
        {materialLabeled ? (
          <ValueText>
            {"Quantidade por embalagem: "}
            <NumberFormat
              value={materialPackingQuantity || 0}
              displayType="text"
              decimalSeparator=","
              thousandSeparator={(true, ".")}
            />
            {" unidades"}
          </ValueText>
        ) : null}
        <StyledTypeOfLabeled
          type={
            materialLabeled
              ? MaterialFractionated.LABELED
              : MaterialFractionated.UNLABELED
          }
        >
          {materialLabeled ? "Padrão caixa" : "Fracionado"}
        </StyledTypeOfLabeled>
      </UnitsWrapper>
      <ControlWrapper minWidth={!isAgentView}>
        {/* {!isAgentView && (
          <SwitchModeContainer>
            <StyledLabel>Qtde por representante</StyledLabel>
            <div>
              <SwitchLabel selected={qtdMode === MaterialQtdMode.MANUAL}>Manual</SwitchLabel>
              <Switch
                color="primary"
                size="small"
                checked={qtdMode === MaterialQtdMode.AUTOMATIC}
                onChange={handleModeChange}
                disabled={agentTotal > availableCount ? true : false}
              />
              <SwitchLabel selected={qtdMode === MaterialQtdMode.AUTOMATIC}>Automático</SwitchLabel>
            </div>
          </SwitchModeContainer>
        )} */}
        {canEdit && handleQtdChange ? (
          <StyledTextField
            name="materialQuantity"
            label="Quantidade"
            value={qty || 0}
            variant="outlined"
            onChange={handleQtdChange}
            onBlur={handleQtdChange}
            component={TextField}
            error={isMaterialFieldError}
            helperText={messageHelperText}
            InputProps={{
              endAdornment: (
                <StyledInputAdornment position="end">
                  <StyledDecreaseIconButton
                    onClick={() => {
                      handleDecrease();
                    }}
                    edge="end"
                  >
                    -
                  </StyledDecreaseIconButton>
                  <StyledIconButton
                    onClick={() => {
                      handleIncrease();
                    }}
                    edge="end"
                  >
                    +
                  </StyledIconButton>
                </StyledInputAdornment>
              ),
            }}
          />
        ) : null}
        {/* {!isAgentView && (
          <>
            <UnitsWrapper>
              <StyledLabel error={total > availableCount}>Total</StyledLabel>
              <ValueText error={total > availableCount}>{total || 0}</ValueText>
            </UnitsWrapper>
          </>
        )} */}
      </ControlWrapper>
      <ActionButtonContainer>
        {/* {!isAgentView && handleEdit ? (
          <IconButton
            color="primary"
            aria-label="Editar material"
            onClick={handleEdit}
          >
            <Edit />
          </IconButton>
        ) : null} */}
        {handleRemove ? (
          <IconButton
            color="primary"
            aria-label="Remover material"
            onClick={handleRemove}
          >
            <Delete />
          </IconButton>
        ) : null}
      </ActionButtonContainer>
    </Container>
  );
}

SelectedMaterialRow.propTypes = {
  availableCount: PropTypes.number,
  canEdit: PropTypes.bool,
  handleEdit: PropTypes.func,
  handleQtdChange: PropTypes.func,
  handleModeChange: PropTypes.func,
  handleRemove: PropTypes.func,
  qtdMode: PropTypes.number,
  title: PropTypes.string,
  total: PropTypes.number,
  isMaterialFieldError: PropTypes.bool,
  messageHelperText: PropTypes.string,
};

SelectedMaterialRow.defaultProps = {
  availableCount: 0,
  canEdit: false,
  handleEdit: null,
  handleQtdChange: null,
  handleModeChange: () => {},
  handleRemove: null,
  qtdMode: MaterialQtdMode.AUTOMATIC,
  title: "",
  total: 0,
  isMaterialFieldError: false,
  messageHelperText: "",
};

export default SelectedMaterialRow;
